import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Exam, ArrowLeft, ArrowRight, BookOpen, Wrench, Medal, Certificate } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/contexts/LanguageContext";
import { useQuizBadges } from "@/contexts/QuizBadgesContext";
import { LESSON_QUIZZES, getLessonQuiz } from "@/data/quizzes";
import { LEARNING_PATH } from "@/data/learningPath";
import LessonQuiz from "@/components/quiz/LessonQuiz";
import PrintQuizButton from "@/components/quiz/PrintQuizButton";
import Leaderboard from "@/components/quiz/Leaderboard";
import ChampionCard from "@/components/quiz/ChampionCard";
import JoinClassCard from "@/components/quiz/JoinClassCard";
import PracticeStreakBadge from "@/components/quiz/PracticeStreakBadge";

const QUIZ_LIST = LEARNING_PATH.filter((l) => LESSON_QUIZZES[l.to]);

function QuizCard({ item, i, es, t, earned, onStart }) {
  const { to, labelKey, Icon } = item;
  const slug = to.replace(/\//g, "");
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.6) }}
      className={`bg-[#121214]/80 backdrop-blur-md border rounded-xl p-4 flex flex-col gap-3 hover:border-[#F5A524]/40 transition-colors ${earned ? "border-emerald-500/30" : "border-white/10"}`}
      data-testid={`quiz-card-${slug}`}
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#F5A524]/10 shrink-0">
          <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-white font-medium text-sm leading-snug">{t(labelKey)}</p>
          <p className="text-[#71717A] text-xs">{LESSON_QUIZZES[to].en.length} {es ? "preguntas" : "questions"}</p>
        </div>
        {earned && (
          <Medal weight="fill" className="w-5 h-5 text-emerald-400 shrink-0" data-testid={`quiz-badge-${slug}`} />
        )}
      </div>
      <div className="flex items-center gap-2 mt-auto flex-wrap">
        <Button size="sm" onClick={() => onStart(item)} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium" data-testid={`quiz-start-${slug}`}>
          {es ? "Comenzar" : "Start quiz"}
          <ArrowRight weight="bold" className="w-4 h-4 ml-1" />
        </Button>
        <PrintQuizButton path={to} title={t(labelKey)} testId={`quiz-print-${slug}`} compact />
        <Link to={to} className="text-[#A1A1AA] hover:text-white text-xs flex items-center gap-1 ml-auto" data-testid={`quiz-lesson-link-${slug}`}>
          <BookOpen className="w-3.5 h-3.5" />
          {es ? "Ver lección" : "Open lesson"}
        </Link>
      </div>
    </motion.div>
  );
}

function ExamBanner({ es }) {
  return (
    <Link
      to="/exam"
      className="flex items-center gap-4 bg-gradient-to-r from-[#F5A524]/15 via-[#121214] to-[#121214] border border-[#F5A524]/40 rounded-xl p-4 sm:p-5 hover:border-[#F5A524]/70 transition-colors group"
      data-testid="quiz-exam-banner"
    >
      <Certificate weight="fill" className="w-8 h-8 text-[#F5A524] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-white font-semibold">{es ? "Examen Final del Curso" : "Final Course Exam"}</p>
        <p className="text-[#A1A1AA] text-sm">
          {es ? "30 preguntas de todas las lecciones · aprueba con 80% y obtén tu certificado." : "30 questions from every lesson · pass with 80% and earn your certificate."}
        </p>
      </div>
      <ArrowRight weight="bold" className="w-5 h-5 text-[#F5A524] group-hover:translate-x-1 transition-transform" />
    </Link>
  );
}

export default function QuizzesPage() {
  const { t, lang } = useTranslation();
  const es = lang === "es";
  const { badges, markPerfect, practiceClears } = useQuizBadges();
  const [active, setActive] = useState(null);
  const earnedCount = QUIZ_LIST.filter((q) => badges.has(q.to)).length;

  const start = (item) => {
    setActive(item);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onFinish = async (score, total) => {
    if (active && score === total && (await markPerfect(active.to))) {
      toast.success(es ? "¡Insignia ganada! 5/5 en esta lección" : "Badge earned! 5/5 on this lesson");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8" data-testid="quizzes-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="flex items-center gap-1.5 text-[#F5A524] text-xs uppercase tracking-[0.2em] mb-2">
            <Exam weight="fill" className="w-4 h-4" />
            {es ? "Aprender" : "Learn"}
          </p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl text-white font-semibold tracking-tight mb-2">{es ? "Cuestionarios" : "Quizzes"}</h1>
          <p className="text-[#71717A] text-base">
            {es
              ? `${QUIZ_LIST.length} cuestionarios, uno por lección. Saca 5/5 para ganar la insignia de la lección.`
              : `${QUIZ_LIST.length} quizzes, one per lesson. Score 5/5 to earn that lesson's badge.`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <PracticeStreakBadge count={practiceClears} size="lg" testId="quizzes-practice-streak" />
        <div className="flex items-center gap-2 bg-[#121214]/80 border border-white/10 rounded-xl px-4 py-2.5" data-testid="quiz-badges-count">
          <Medal weight="fill" className="w-5 h-5 text-emerald-400" />
          <span className="font-['JetBrains_Mono'] text-white">{earnedCount}/{QUIZ_LIST.length}</span>
          <span className="text-[#71717A] text-sm">{es ? "insignias" : "badges"}</span>
        </div>
        </div>
      </div>

      {active ? (
        <div className="space-y-4" data-testid="quiz-active">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-['Outfit'] text-white text-xl font-semibold" data-testid="quiz-active-title">{t(active.labelKey)}</h2>
            <div className="flex items-center gap-2">
              <PrintQuizButton path={active.to} title={t(active.labelKey)} testId="quiz-active-print" />
              <Button variant="outline" size="sm" onClick={() => setActive(null)} className="border-white/15 text-[#D4D4D8] hover:bg-white/5" data-testid="quiz-back-btn">
                <ArrowLeft weight="bold" className="w-4 h-4 mr-1.5" />
                {es ? "Todos los cuestionarios" : "All quizzes"}
              </Button>
            </div>
          </div>
          <LessonQuiz key={`${active.to}-${lang}`} questions={getLessonQuiz(active.to, lang)} testPrefix="page-quiz" onFinish={onFinish} />
        </div>
      ) : (
        <>
          <ExamBanner es={es} />
          <JoinClassCard />
          <ChampionCard />
          <Leaderboard />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {QUIZ_LIST.map((item, i) => (
              <QuizCard key={item.to} item={item} i={i} es={es} t={t} earned={badges.has(item.to)} onStart={start} />
            ))}
          </div>
          <Link
            to="/tools"
            className="flex items-center gap-3 bg-[#0A0A0A] border border-dashed border-white/15 rounded-xl p-4 hover:border-[#F5A524]/40 transition-colors"
            data-testid="quiz-general-link"
          >
            <Wrench weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <span className="text-[#A1A1AA] text-sm flex-1">
              {es ? "¿Quieres un repaso general? Prueba el Quiz de Conocimiento Fotográfico en Herramientas." : "Want a general review? Try the Photography Knowledge Quiz in Tools."}
            </span>
            <ArrowRight className="w-4 h-4 text-[#52525B]" />
          </Link>
        </>
      )}
    </div>
  );
}
